import React, { useEffect, useState, useRef } from 'react'
import { Form, Row, Col } from 'react-bootstrap';
import { useForm } from "react-hook-form";
import { billingInformationsState, updateBillingInformation, resetUpdated, getBillingInformation } from '../billingInformationsSlice';
import { resetVat } from '../../planUpgrade/planUpgradeSlice';
import { useAppDispatch, useAppSelector } from '../../../hooks/redux/hooks';
import { useCountry, useCountryState } from '../../../hooks/country/useCountry';
import SmashSpinner from '../../../components/SmashSpinner/SmashSpinner';
import { removeEmptyFields } from '../../../shared/helpers/helpers';
import SmashSelect from '../../../components/SelectInput/SmashSelect';

type FormValues = {
    firstName: string;
    lastName: string;
    company?: string;
    city: string;
    countryCode: string;
    country: string;
    stateCode?: string;
    state?: string;
    zipCode: string;
    address: string;
    vatNumber?: string;
};

const BillingInformationForm = ({ informationFormId, isFormDirty, nextStep }: { informationFormId: string, isFormDirty?: (isDirty: boolean) => void, nextStep?: () => void }) => {
    const dispatch = useAppDispatch();
    const [isCountrySuccess, countries] = useCountry();
    const [countryStates, updateStateOptions] = useCountryState(countries);
    const { billingInformation } = useAppSelector(billingInformationsState);
    const { informations,
        isLoading: isBillingInformationLoading,
        isUpdated: isBillingInformationUpdated,
        isSuccess: isBillingInformationSuccess,
        error,
        isError: isBillingInformationError } = billingInformation;

    const { handleSubmit, setValue, formState: { errors, isDirty }, register, reset, setError, control, } = useForm<FormValues>({
        mode: "onSubmit",
        reValidateMode: "onChange",
    });

    const submitHandler = (values: FormValues) => {
        dispatch(resetVat());
        dispatch(updateBillingInformation(removeEmptyFields(values)));
    }

    const [stateLoaded, setStateLoaded] = useState(false);

    const infoRef = useRef<FormValues | null>();

    useEffect(() => {
        if (isDirty && isFormDirty) { // if user has no information we can get a 404
            isFormDirty(isDirty);
        }
    }, [isDirty])

    useEffect(() => {
        if (!isBillingInformationSuccess) { // if user has no information we can get a 404
            dispatch(getBillingInformation());
        }
    }, [])

    useEffect(() => {
        // Handle errors back from server on vatNumber
        if (isBillingInformationError && informations) {
            reset(informations);
            setError('vatNumber', { type: 'custom', message: error?.message });
        }

        if (isBillingInformationUpdated && isBillingInformationSuccess && nextStep) {
            nextStep();
        }
    }, [informations, isBillingInformationError, isBillingInformationUpdated, isBillingInformationSuccess])

    useEffect(() => {
        if (!stateLoaded && informations) {
            reset(informations); // set default value if any
            setStateLoaded(true);
            updateStateOptions(informations.countryCode);
        }

        if (informations && informations !== infoRef.current) {
            infoRef.current = informations;
            // to refresh the other form that might be displayed
            reset(informations);
            updateStateOptions(informations.countryCode);
        }

        if (isBillingInformationUpdated) {
            dispatch(resetUpdated());
        }

    }, [stateLoaded, informations, isBillingInformationUpdated, updateStateOptions]);

    const selectCountryChange: any = ({ label, value }: any) => {
        setValue('countryCode', value);
        setValue('country', label);
        setValue('stateCode', undefined);
        setValue('state', undefined);
        updateStateOptions(value);
    };

    const selectStateChange: any = ({ label, value }: any) => {
        setValue('stateCode', value);
        setValue('state', label);
    };

    const countryOptions = countries.map(({ countryCode, label }) => ({ value: countryCode, label }));

    if (isBillingInformationLoading) {
        return (<SmashSpinner />)
    }

    return (
        <>
            <Form id={informationFormId} noValidate onSubmit={handleSubmit(submitHandler)}>
                <Row>
                    <Col>
                        <Form.Group className="mb-3" controlId="firstName">
                            <Form.Control
                                {...register('firstName', { required: true })}
                                type="text"
                                placeholder="First name"
                                isInvalid={!!errors.firstName}
                            />
                            {errors.firstName?.type === "required" && (
                                <Form.Control.Feedback type="invalid">
                                    First name is required
                                </Form.Control.Feedback>)}
                        </Form.Group>
                    </Col>
                    <Col>
                        <Form.Group className="mb-3" controlId="lastName">
                            <Form.Control
                                {...register('lastName', { required: true })}
                                type="text"
                                placeholder="Last name"
                                isInvalid={!!errors.lastName}
                            />
                            {errors.lastName?.type === "required" && (
                                <Form.Control.Feedback type="invalid">
                                    Last name is required
                                </Form.Control.Feedback>)}
                        </Form.Group>
                    </Col>
                </Row>
                <Row>
                    <Col>
                        <Form.Group className="mb-3" controlId="company">
                            <Form.Control
                                {...register('company')}
                                type="text"
                                placeholder="Company name (optional)"
                                isInvalid={!!errors.company}
                            />
                        </Form.Group>
                    </Col>
                </Row>
                <Row>
                    <Col>
                        <Form.Group className="mb-3" controlId="city">
                            <Form.Control
                                {...register('address', { required: true })}
                                type="text"
                                placeholder="Address"
                                isInvalid={!!errors.address}
                            />
                            {errors.address?.type === "required" && (
                                <Form.Control.Feedback type="invalid">
                                    City is required
                                </Form.Control.Feedback>)}
                        </Form.Group>
                    </Col>
                </Row>
                <Row>
                    <Col className="col-4">
                        <Form.Group className="mb-3" controlId="city">
                            <Form.Control
                                {...register('city', { required: true })}
                                type="text"
                                placeholder="City"
                                isInvalid={!!errors.city}
                            />
                            {errors.city?.type === "required" && (
                                <Form.Control.Feedback type="invalid">
                                    City is required
                                </Form.Control.Feedback>)}
                        </Form.Group>
                    </Col>
                    <Col>
                        <Form.Group className="mb-3" controlId="countryCode">
                            <SmashSelect
                                rules={{ required: true }}
                                control={control}
                                name={'countryCode'}
                                defaultValue={informations?.countryCode}
                                placeholder="Select a country"
                                options={countryOptions}
                                onChangeHook={selectCountryChange}
                                isInvalid={!!errors.countryCode}
                            />
                            {errors.countryCode?.type === "required" && (
                                <Form.Control.Feedback type="invalid">
                                    Country is required
                                </Form.Control.Feedback>)}
                        </Form.Group>
                    </Col>
                </Row>
                <Row>
                    {countryStates && <Col>
                        <Form.Group className="mb-3" controlId="state">
                            <SmashSelect
                                rules={{ required: true }}
                                control={control}
                                name={'stateCode'}
                                defaultValue={informations?.stateCode}
                                placeholder="Select a state"
                                options={countryStates}
                                onChangeHook={selectStateChange}
                                isInvalid={!!errors.stateCode}
                            />
                            {errors.stateCode?.type === "required" && (
                                <Form.Control.Feedback type="invalid">
                                    State is required
                                </Form.Control.Feedback>)}
                        </Form.Group>
                    </Col>}
                </Row>
                <Row>
                    <Col>
                        <Form.Group className="mb-3" controlId="zipCode">
                            {/* only for us */}
                            <Form.Control
                                {...register('zipCode', { required: true })}
                                type="text"
                                placeholder="Postal Code"
                                isInvalid={!!errors.zipCode}
                            />
                            {errors.zipCode?.type === "required" && (
                                <Form.Control.Feedback type="invalid">
                                    Postal code is required
                                </Form.Control.Feedback>)}
                        </Form.Group>
                    </Col>
                </Row>
                <Row>
                    <Col>
                        <Form.Group className="mb-3" controlId="vatNumber">
                            <Form.Control
                                {...register('vatNumber')}
                                type="text"
                                placeholder="VAT (optional)"
                                isInvalid={!!errors.vatNumber}
                            />
                            {errors.vatNumber?.type === "custom" && (
                                <Form.Control.Feedback type="invalid">
                                    {errors.vatNumber?.message}
                                </Form.Control.Feedback>)}

                        </Form.Group>
                    </Col>
                </Row>
            </Form>
        </>
    )
}

export default BillingInformationForm